import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import FormControl from '@material-ui/core/FormControl';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import Drawer from '@material-ui/core/Drawer';
import { AppBar, CssBaseline, Toolbar, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useHistory, useLocation } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import { AssignmentTurnedInOutlined, EmailOutlined, ExitToApp } from '@material-ui/icons';
import MenuIcon from '@material-ui/icons/Menu';
import DashboardIcon from '../icons/Dashboard-icon';
import BiddingIcon from '../icons/Bidding-icon';
import DesignIcon from '../icons/Design-icon';
import SparkleIcon from '../icons/Sparkle-icon';
import ConstructionIcon from '../icons/Construction-icon';
import DrawerListItem from './DrawerListItem';
import DrawerNestedList from './DrawerNestedList';
import { centerlineAiPath, useCenterlineAiAvailable } from '../centerline-ai/centerline-ai-access';
import './NavDrawer.scss';
import DashboardLockup from '../icons/Dashboard-lockup';
import { Auth } from '@aws-amplify/auth';
import { setUser } from '../../features/user/actions';
import { getAllProjectsIncludingArchivedState } from '../../features/projects/selectors';
import { setProject } from '../../features/project/actions';
import ArchivedProjectLock from '../custom-components/ArchivedProjectLock';
import {
  getCurrentProjectSubscriberId,
  getCurrentSubscriberLogoFileId,
  getProjectState,
} from '../../features/project/selectors';
import { getUserState } from '../../features/user/selectors';
import { getCurrentSecurityGroup } from '../../features/security/selectors';
import CloseOutIcon from '../icons/CloseOut-icon';
import {
  canViewBidding,
  canViewConstructionAndCloseout,
  canViewDesign,
  getProjectDisplayName,
  isPdfViewer,
  isPublicPage,
  projectSort,
} from '../../scripts/utils';
import { hasAdminPermissions, hasCompletedConstructionSetup } from '../../scripts/store-utils';
import { getProductPackageState } from '../../features/product-package/selectors';
import { DocumentTemplateType } from '../../api-client/autogenerated';
import { getDocumentsType } from '../../features/documents/selectors';
import { Autocomplete } from '@material-ui/lab';
import TextField from '@material-ui/core/TextField';
import { getFileUrlById } from '../../models/api/files';

export const PRIMARY_NAVIGATION_WIDTH_OPEN = 260;
export const PRIMARY_NAVIGATION_WIDTH_CLOSED = 60;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: PRIMARY_NAVIGATION_WIDTH_OPEN,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerOpen: {
    overflowX: 'hidden',
    width: PRIMARY_NAVIGATION_WIDTH_OPEN,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    background: 'linear-gradient(225deg, #00308C 0%, #002366 100%)',
    'box-shadow': '0px 0px 10px rgba(0, 0, 0, 0.1)',
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    [theme.breakpoints.up('xs')]: {
      width: PRIMARY_NAVIGATION_WIDTH_CLOSED,
    },
    background: 'linear-gradient(225deg, #00308C 0%, #002366 100%)',
    'box-shadow': '0px 0px 10px rgba(0, 0, 0, 0.1)',
  },
  toolbarClosed: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  },
  toolbarOpen: {
    display: 'flex',
    alignItems: 'right',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  },
  // align logo to arrow
  marginAutoItem: {
    marginBottom: 35,
    marginTop: 30,
    marginRight: 1,
    position: 'absolute',
  },
  selectAnchor: {
    position: 'relative',
    top: 20,
  },
  formControl: {
    display: 'flex',
    width: '100%',
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  drawerArrow: {
    color: '#A6C4FF',
    textAlign: 'right',
  },
  optionLock: {
    marginRight: 6,
  },
  selectedProjectLock: {
    marginLeft: 4,
  },
  drawerSelect: {
    height: '32px',
    /* Gray / Gray 50 */
    background: '#F9F9F9',

    /* Gray / Gray 400 Brand dark */
    border: '1px solid #949494',
    'border-radius': '5px',
  },
  drawerDivider: {
    marginTop: '10px',
    marginBottom: '10px',
  },
  menuPaper: {
    maxHeight: 400,
    maxWidth: 300,
    overflowWrap: 'break-word',
    whiteSpace: 'unset',
  },
  wrap: {
    whiteSpace: 'unset',
    overflowWrap: 'break-word',
  },
  dropdownPadding: {
    paddingTop: 40,
  },
  exitButton: {
    margin: 'auto 0px 12px 5px',
    alignSelf: 'flex-start',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '18px',
    lineHeight: '25px',
    textTransform: 'none',
    color: '#CCDEFF',
  },
  hidden: {
    display: 'none',
    width: 0,
  },

  appBar: {
    [theme.breakpoints.up('sm')]: {
      width: `calc(100% - ${PRIMARY_NAVIGATION_WIDTH_OPEN}px)`,
      marginLeft: PRIMARY_NAVIGATION_WIDTH_OPEN,
    },
    background: '#00308C',
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  searchBarIcon: {
    marginRight: -6,
  },
  searchBarPopper: {
    minWidth: '200px !important',
    maxWidth: '300px !important',
    width: 'unset !important',
    boxShadow: '3px 5px 5px 2px rgba(0, 0, 0, 0.20)',
    marginLeft: 6,
    marginTop: -4,
  },
  hideClearIcon: {
    display: 'none',
  },
}));

type Props = {
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function NavDrawer(props: Props) {
  const classes = useStyles();
  const { drawerOpen, setDrawerOpen } = props;

  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const currentProject = useSelector(getProjectState);
  const currentSubscriberId = useSelector(getCurrentProjectSubscriberId);
  const currentSubscriberLogoFileId = useSelector(getCurrentSubscriberLogoFileId);
  const user = useSelector(getUserState);
  // The selector is how a user reaches an archived project, so it lists them alongside the rest.
  const projects = useSelector(getAllProjectsIncludingArchivedState);
  const security = useSelector(getCurrentSecurityGroup);
  const productPackage = useSelector(getProductPackageState);

  const [selectProjectInput, setSelectProjectInput] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [emailMenuOpen, setEmailMenuOpen] = useState(false);
  const [designMenuOpen, setDesignMenuOpen] = useState(false);
  const [biddingMenuOpen, setBiddingMenuOpen] = useState(false);
  const [closeOutMenuOpen, setCloseOutMenuOpen] = useState(false);
  const [constructionMenuOpen, setConstructionMenuOpen] = useState(false);
  const [storytellingMenuOpen, setStorytellingMenuOpen] = useState(false);

  const [drawerIndex, setDrawerIndex] = useState(-1);
  const [favoriteProjectIds, setFavoriteProjectIds] = useState<string[]>([]);
  const [subscriberLogoLink, setSubscriberLogoLink] = useState('');
  const documentType = useSelector(getDocumentsType);

  const pathnames = location.pathname.split('/').filter((x) => {
    if (documentType === DocumentTemplateType.PunchList)
      return x && x !== 'edit' && x !== 'edit-location';
    return x;
  });

  useEffect(() => {
    if (user) {
      setFavoriteProjectIds(user.favoriteProjectList?.map((favorite) => favorite.projectId) || []);
    }
  }, [user]);

  useEffect(() => {
    if (currentSubscriberLogoFileId) {
      const fetchSubscriberLogoLink = async () => {
        try {
          const logoFileResponse = await getFileUrlById(currentSubscriberLogoFileId, true);
          setSubscriberLogoLink(logoFileResponse.downloadLink || '');
          console.log('Subscriber logo link fetched:', logoFileResponse.downloadLink);
        } catch (error) {
          console.error('Failed to fetch subscriber info:', error);
        }
      };

      fetchSubscriberLogoLink();
    } else {
      setSubscriberLogoLink('');
    }
  }, [currentSubscriberId]);

  const emailMenuItems = ['My Notification Inbox'];
  if (
    currentProject?.emailSlug &&
    ((security?.correspondenceInboxPermission !== null &&
      security?.correspondenceInboxPermission !== undefined) ||
      user.isSiteAdmin ||
      user.adminOfSubscriberId === currentProject?.subscriberId)
  ) {
    emailMenuItems.push('Correspondence Log');
  }

  const designMenuItems = [
    'Design Task Manager',
    'Design Meeting Minutes',
    'Design Package Review',
  ];
  if (canViewDesign(productPackage, security, user, currentProject)) {
    designMenuItems.splice(0, 0, 'CENTERLINE Drive');
  }

  if (hasAdminPermissions(user) || !!security?.manageProjectUsersPermission) {
    designMenuItems.splice(0, 0, 'Manage Project');
  }

  const biddingMenuItems = currentProject?.bidSetupId
    ? [
        'Bid Drawings',
        'Project Manual',
        'Addenda',
        'Informational Items',
        'Bidder RFIs',
        'Substitution Requests',
        // 'Regulatory Approvals',
        'CENTERLINE BidConnect',
      ]
    : [];
  if (
    user.isSiteAdmin ||
    user.adminOfSubscriberId === currentProject?.subscriberId ||
    (security && security.bidManagementPermission! > 0)
  ) {
    biddingMenuItems.splice(0, 0, currentProject?.bidSetupId ? 'Bid Management' : 'Bid Setup');
  }
  if (
    currentProject?.bidSetupId &&
    (user.isSiteAdmin ||
      user.adminOfSubscriberId === currentProject?.subscriberId ||
      (security && security.planholderListPermission! > 0))
  )
    biddingMenuItems.splice(biddingMenuItems.length - 3, 0, 'Planholder List');
  if (
    currentProject?.bidSetupId &&
    (user.isSiteAdmin ||
      user.adminOfSubscriberId === currentProject?.subscriberId ||
      (security && security.bidTabulationPermission! > 0))
  )
    biddingMenuItems.splice(biddingMenuItems.length - 1, 0, 'Bid Submissions');

  const constructionMenuItems =
    currentProject && hasCompletedConstructionSetup()
      ? [
          'Drawings',
          'Specifications',
          'ASI Documents',
          'CA Task Manager',
          'Submittals',
          'Requests for Information',
          'Change Orders',
          'Const. Change Directives',
          'Potential Change Orders',
          'Requests for Change',
          'Work Change Proposal Req.',
          'Pay Applications',
          'Field Reports',
          'CA Meeting Minutes',
          'Contractor Daily Logs',
          'Miscellaneous Documents',
          'Schedules',
          'Testing',
        ]
      : [];
  if (
    hasCompletedConstructionSetup() &&
    (user.isSiteAdmin ||
      user.adminOfSubscriberId === currentProject?.subscriberId ||
      (security && security.constructionManagementPermission! > 0))
  )
    constructionMenuItems.splice(0, 0, 'Construction Management');
  else if (
    user.isSiteAdmin ||
    user.adminOfSubscriberId === currentProject?.subscriberId ||
    (security && security.constructionManagementPermission! > 0)
  )
    constructionMenuItems.splice(0, 0, 'Construction Setup');
  if (
    hasCompletedConstructionSetup() &&
    (user.isSiteAdmin ||
      user.adminOfSubscriberId === currentProject?.subscriberId ||
      (security && security.publishingCenterPermission && security.publishingCenterPermission > 0))
  ) {
    const canSeeConstructionManagement =
      constructionMenuItems.indexOf('Construction Management') !== -1;
    constructionMenuItems.splice(canSeeConstructionManagement ? 1 : 0, 0, 'Publishing Center');
  }

  const closeOutMenuItems =
    currentProject && currentProject.architectAdministratorUserId
      ? [
          'Punch List',
          'As-Built Drawings',
          'Closeout Submittals',
          'O&M Data',
          'Warranty Items',
          'Clear L&P',
          'Substantial Completion Cert.',
        ]
      : [];

  const storytellingMenuItems = [
    'Project Lifecycle',
    'Project Closeout',
    'Project Progress Reports',
    'Quality Control Forms',
    'A3 Summary',
  ];

  const openDrawerIcon =
    theme.direction === 'rtl' ? (
      <ArrowBackIcon className={classes.drawerArrow} />
    ) : (
      <ArrowForwardIcon className={classes.drawerArrow} />
    );
  const closeDrawerIcon =
    theme.direction === 'rtl' ? (
      <ArrowForwardIcon className={classes.drawerArrow} />
    ) : (
      <ArrowBackIcon className={classes.drawerArrow} />
    );

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleProjectSelect = (projectId: string) => {
    setDrawerIndex(-1);
    if (projectId === 'none') {
      setSelectedProjectId(projectId);
      dispatch(setProject());
      history.push('/main');
    } else if (projectId === 'create') {
      setSelectedProjectId('');
      dispatch(setProject());
      history.push('/main/project');
    } else {
      setSelectedProjectId(projectId);
      history.push(`/main/projects/${projectId}`);
    }
  };

  const toggleEmailMenu = () => {
    setDrawerOpen(true);
    setEmailMenuOpen(!drawerOpen ? true : !emailMenuOpen);
    setDesignMenuOpen(false); // set true if drawer is closed
    setBiddingMenuOpen(false);
    setConstructionMenuOpen(false);
    setCloseOutMenuOpen(false);
    setStorytellingMenuOpen(false);
  };

  const toggleDesignMenu = () => {
    setDrawerOpen(true);
    setDesignMenuOpen(!drawerOpen ? true : !designMenuOpen); // set true if drawer is closed
    setBiddingMenuOpen(false);
    setConstructionMenuOpen(false);
    setEmailMenuOpen(false);
    setCloseOutMenuOpen(false);
    setStorytellingMenuOpen(false);
  };

  const toggleBiddingMenu = () => {
    setDrawerOpen(true);
    setBiddingMenuOpen(!drawerOpen ? true : !biddingMenuOpen); // set true if drawer is closed
    setCloseOutMenuOpen(false);
    setConstructionMenuOpen(false);
    setDesignMenuOpen(false);
    setEmailMenuOpen(false);
    setStorytellingMenuOpen(false);
  };

  const toggleCloseOutMenu = () => {
    setDrawerOpen(true);
    setCloseOutMenuOpen(!drawerOpen ? true : !closeOutMenuOpen); // set true if drawer is closed
    setBiddingMenuOpen(false);
    setConstructionMenuOpen(false);
    setDesignMenuOpen(false);
    setEmailMenuOpen(false);
    setStorytellingMenuOpen(false);
  };

  const toggleConstructionMenu = () => {
    setDrawerOpen(true);
    setConstructionMenuOpen(!drawerOpen ? true : !constructionMenuOpen); // set true if drawer is closed
    setBiddingMenuOpen(false);
    setCloseOutMenuOpen(false);
    setDesignMenuOpen(false);
    setEmailMenuOpen(false);
    setStorytellingMenuOpen(false);
  };

  const toggleStorytellingMenu = () => {
    setDrawerOpen(true);
    setStorytellingMenuOpen(!drawerOpen ? true : !storytellingMenuOpen); // set true if drawer is closed
    setBiddingMenuOpen(false);
    setCloseOutMenuOpen(false);
    setConstructionMenuOpen(false);
    setDesignMenuOpen(false);
    setEmailMenuOpen(false);
  };

  const handleDrawerItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number,
  ) => {
    //setDrawerIndex(index);
  };

  const initiateSelectedIndex = (setIndex: (index: number) => void, area: string) => {
    const emailMenuItems = currentProject ? ['my-notifications'] : [];
    if (currentProject?.emailSlug) {
      emailMenuItems.push('correspondence-log');
    }
    const designMenuItems = [
      'manage',
      'design',
      'tasks',
      'design-packages',
      'design-meeting-minutes',
    ];
    const biddingMenuItems = [
      'bid-mgmt',
      'bid-drawings',
      'project-manual',
      'addenda',
      'info-items',
      'substitution-requests',
      'bidder-rfis',
      'planholder-list',
      'bid-tabulation',
      'regulatory-approvals',
    ];
    const constructionMenuItems = [
      'const-mgmt',
      'pub-center',
      'drawings',
      'specifications',
      'asi-docs',
      'submittals',
      'rfi',
      'change-orders',
      'const-change-directives',
      'potential-change-orders',
      'rfc',
      'work-change-prop-requests',
      'pay-applications',
      'field-reports',
      'ca-tasks',
      'meeting-minutes',
      'daily-logs',
      'misc-docs',
      'schedules',
      'testing',
    ];
    const closeoutMenuItems = [
      'punch-list',
      'as-built',
      'closeout-submittals',
      'om-data',
      'clear-lp',
      'substantial-completion-cert',
    ];
    const storytellingMenuItems = [
      'project-lifecycle',
      'closeout',
      'progress',
      'project-quality-control-forms',
      'a3',
    ];

    if (pathnames.length === 3) {
      setIndex(0);
    } else if (pathnames.length > 1 && area === 'sidebar') {
      let lastPathName = pathnames[pathnames.length - 1];
      let innerPathName = pathnames[pathnames.length - 2];
      //need a special one for 1) drawings, 2) specs, 3) asi docs, 4) submittals, 5) rfi, 6) Change orders,

      // CENTERLINE AI's page (/main/centerline-ai) belongs to its own entry, not to Dashboard.
      if (lastPathName === 'centerline-ai') setIndex(1);
      else if (emailMenuItems.includes(lastPathName) || emailMenuItems.includes(innerPathName))
        setIndex(2);
      else if (designMenuItems.includes(lastPathName) || designMenuItems.includes(innerPathName))
        setIndex(3);
      else if (biddingMenuItems.includes(lastPathName) || biddingMenuItems.includes(innerPathName))
        setIndex(4);
      else if (
        constructionMenuItems.includes(lastPathName) ||
        constructionMenuItems.includes(innerPathName)
      )
        setIndex(5);
      else if (
        closeoutMenuItems.includes(lastPathName) ||
        closeoutMenuItems.includes(innerPathName)
      )
        setIndex(6);
      else if (
        storytellingMenuItems.includes(lastPathName) ||
        storytellingMenuItems.includes(innerPathName)
      )
        setIndex(7);
    } else if (pathnames.length > 1 && area === 'items') {
      let lastPathName = pathnames[pathnames.length - 1];
      let innerPathName = pathnames[pathnames.length - 2];
      if (emailMenuItems.includes(lastPathName)) setIndex(emailMenuItems.indexOf(lastPathName));
      else if (designMenuItems.includes(lastPathName))
        setIndex(designMenuItems.indexOf(lastPathName));
      else if (biddingMenuItems.includes(lastPathName))
        setIndex(biddingMenuItems.indexOf(lastPathName));
      else if (constructionMenuItems.includes(lastPathName))
        setIndex(constructionMenuItems.indexOf(lastPathName));
      else if (closeoutMenuItems.includes(lastPathName))
        setIndex(closeoutMenuItems.indexOf(lastPathName));
      else if (storytellingMenuItems.includes(lastPathName))
        setIndex(storytellingMenuItems.indexOf(lastPathName));
      else if (emailMenuItems.includes(innerPathName))
        setIndex(emailMenuItems.indexOf(innerPathName));
      else if (designMenuItems.includes(innerPathName))
        setIndex(designMenuItems.indexOf(innerPathName));
      else if (biddingMenuItems.includes(innerPathName))
        setIndex(biddingMenuItems.indexOf(innerPathName));
      else if (constructionMenuItems.includes(innerPathName))
        setIndex(constructionMenuItems.indexOf(innerPathName));
      else if (closeoutMenuItems.includes(innerPathName))
        setIndex(closeoutMenuItems.indexOf(innerPathName));
      else if (storytellingMenuItems.includes(innerPathName))
        setIndex(storytellingMenuItems.indexOf(innerPathName));
    }
  };

  const canViewProjectLifecycle = () => {
    return (
      currentProject?.subscriber?.canViewProjectLifecycle &&
      ((security?.projectLifecyclePermission !== null &&
        security?.projectLifecyclePermission !== undefined) ||
        user.isSiteAdmin ||
        user.adminOfSubscriberId === currentProject?.subscriberId)
    );
  };

  // CENTERLINE AI: on a project where the backend says the user can open it (site admins only,
  // for now). The entry is as on dev/integration, above Email.
  const centerlineAiAvailable = useCenterlineAiAvailable(currentProject?.id);
  const handleAIClick = () => {
    if (!currentProject) return;
    // The page isn't tied to a project: it opens on this one, the project open in CENTERLINE.
    history.push(centerlineAiPath(currentProject.id));
    setEmailMenuOpen(false);
    setDesignMenuOpen(false);
    setBiddingMenuOpen(false);
    setConstructionMenuOpen(false);
    setCloseOutMenuOpen(false);
    setStorytellingMenuOpen(false);
  };

  const handleDashboardClick = () => {
    history.push(`/main${currentProject ? `/projects/${currentProject.id}` : ''}`);
    setBiddingMenuOpen(false);
    setConstructionMenuOpen(false);
    setCloseOutMenuOpen(false);
    setStorytellingMenuOpen(false);
    setDrawerIndex(-1);
  };

  const handleMyNotificationsClick = () => {
    history.push('/main/my-notifications');
    setBiddingMenuOpen(false);
    setConstructionMenuOpen(false);
    setCloseOutMenuOpen(false);
    setStorytellingMenuOpen(false);
    setDrawerIndex(-1);
  };

  const handleMyWorkClick = () => {
    history.push(`/main${currentProject ? `/projects/${currentProject.id}` : ''}/my-work`);
    setBiddingMenuOpen(false);
    setConstructionMenuOpen(false);
    setCloseOutMenuOpen(false);
    setStorytellingMenuOpen(false);
    setDrawerIndex(-1);
  };

  useEffect(() => {
    setDrawerIndex(-1);
    if (currentProject) {
      setSelectedProjectId(currentProject.id);
      setSelectProjectInput(getProjectDisplayName(currentProject));
    } else {
      setSelectedProjectId('');
      setSelectProjectInput('');
    }
  }, [currentProject]);

  useEffect(() => {
    if (drawerOpen && history.location.pathname === '/main/project') {
      setSelectedProjectId('');
    }
  }, [drawerOpen, history]);

  useEffect(() => {
    initiateSelectedIndex(setDrawerIndex, 'sidebar');
  });

  const handleSignOut = () => {
    Auth.signOut()
      .then(() => {
        dispatch(setUser());
        dispatch(setProject());
        history.push('/main/login');
      })
      .catch((e) => console.log('error signing out: ', e));
  };

  const openedDrawer = (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
        }}
      >
        <IconButton id="arrow-button" onClick={toggleDrawer} style={{ background: 'transparent' }}>
          {closeDrawerIcon}
        </IconButton>
      </div>

      <IconButton
        style={{ backgroundColor: 'transparent', paddingTop: '30px' }}
        onClick={handleDashboardClick}
      >
        {subscriberLogoLink ? (
          <img
            src={subscriberLogoLink}
            alt="Subscriber Logo"
            style={{
              width: '140px',
              height: 'auto',
              maxHeight: '140px',
              objectFit: 'contain',
            }}
          />
        ) : (
          <DashboardLockup style={{ width: '80px', height: '80px' }} />
        )}
      </IconButton>
    </div>
  );

  const closedDrawer = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div className={classes.toolbarClosed}>
          <IconButton
            id="arrow-button"
            onClick={toggleDrawer}
            style={{ background: 'transparent' }}
          >
            {openDrawerIcon}
          </IconButton>
        </div>
        <IconButton style={{ backgroundColor: 'transparent' }} onClick={handleDashboardClick}>
          <DashboardLockup style={{ width: '40px', height: '40px' }} />
        </IconButton>
      </div>
    </>
  );

  const selectedProject = projects.find((p) => p.id === selectedProjectId);

  /**
   * Whether the field is showing the selected project rather than something being typed into it.
   * Selecting an option resets the input to that option's label, so anything else in the box is a
   * search term -- and a search term is not the project, so it must not wear the project's bold,
   * italics or lock.
   */
  const isShowingSelectedProject =
    !!selectedProject && selectProjectInput === getProjectDisplayName(selectedProject);

  /**
   * How a project's name reads in the selector: bold when it is a favorite, italic when the user
   * reaches it only through their company rather than by being on it. Archived projects are not
   * italicized -- the lock says that, and italics already mean something else here. Shared by the
   * option list and the field so the two never disagree.
   */
  const getProjectNameStyle = (project?: {
    id: string;
    hasOnlyCompanyAccess?: boolean;
  }): React.CSSProperties =>
    project
      ? {
          fontWeight: favoriteProjectIds.includes(project.id) ? 'bold' : undefined,
          fontStyle: project.hasOnlyCompanyAccess ? 'italic' : undefined,
        }
      : {};

  const projectOptions = [
    ...(!!currentProject
      ? [{ id: 'none', name: 'All Projects', hasOnlyCompanyAccess: false, isArchived: false }]
      : []),
    ...(user.isSiteAdmin || user.adminOfSubscriberId !== null
      ? [
          {
            id: 'create',
            name: 'Create New Project',
            hasOnlyCompanyAccess: false,
            isArchived: false,
          },
        ]
      : []),
    // Copied before sorting: `projects` is the Redux array itself, and sort mutates in place.
    ...[...projects].sort((a, b) => projectSort(favoriteProjectIds, a, b)),
  ];

  const drawer = (
    <>
      {drawerOpen ? openedDrawer : closedDrawer}
      <div className={classes.selectAnchor} />
      {drawerOpen && (
        <>
          <Typography
            style={{
              marginTop: 24,
              marginLeft: 16,
              color: '#CCDEFF',
              fontSize: 12,
            }}
          >
            Select a Project
          </Typography>
          <FormControl variant="outlined" className={classes.formControl}>
            <Autocomplete
              size="small"
              options={projectOptions}
              getOptionLabel={(project) => getProjectDisplayName(project)}
              getOptionSelected={(option, value) => option.id === value?.id}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={!!selectProjectInput || !!selectedProjectId ? '' : 'Type to search'}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      shrink: false,
                      style: {
                        transform: 'translate(8px, 12px)',
                        background: 'transparent',
                        color: '#B2B1B2',
                      },
                    }}
                    inputProps={{
                      ...params.inputProps,
                      // The list marks a project bold (favorite) or italic (company-only); carried
                      // onto the field so choosing one does not erase what the list just told you
                      // about it, and dropped again while the box holds a search term.
                      style: {
                        paddingLeft: 3,
                        ...getProjectNameStyle(
                          isShowingSelectedProject ? selectedProject : undefined,
                        ),
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      style: { paddingRight: 24 },
                      startAdornment:
                        isShowingSelectedProject && selectedProject?.isArchived ? (
                          <ArchivedProjectLock className={classes.selectedProjectLock} />
                        ) : undefined,
                    }}
                  />
                );
              }}
              renderOption={(project) => (
                <span
                  key={project.id}
                  className={classes.wrap}
                  style={{ ...getProjectNameStyle(project), width: '100%' }}
                >
                  {project.isArchived && <ArchivedProjectLock className={classes.optionLock} />}
                  {getProjectDisplayName(project)}
                </span>
              )}
              classes={{
                popupIndicator: classes.searchBarIcon,
                popper: classes.searchBarPopper,
                clearIndicator: classes.hideClearIcon,
              }}
              inputValue={selectProjectInput}
              value={projects.find((p) => p.id === selectedProjectId)}
              onInputChange={(event, value) => setSelectProjectInput(value)}
              onChange={(event, value) =>
                value ? handleProjectSelect(value.id) : console.log('no value', value)
              }
            />
          </FormControl>
        </>
      )}
      <Divider className={classes.drawerDivider} />
      <List style={{ padding: 0 }}>
        <DrawerListItem
          key={0}
          title="Dashboard"
          listIndex={0}
          onClick={handleDrawerItemClick}
          selectedIndex={drawerIndex}
          icon={<DashboardIcon />}
          drawerOpen={drawerOpen}
          handlePageChange={handleDashboardClick}
        />
        {/* <Divider className={classes.drawerDivider} /> */}
        {/* Calendar moved to a view toggle on the My Work page. */}
        {centerlineAiAvailable ? (
          <DrawerListItem
            key={1}
            title="CENTERLINE AI"
            listIndex={1}
            onClick={handleDrawerItemClick}
            selectedIndex={drawerIndex}
            icon={<SparkleIcon />}
            drawerOpen={drawerOpen}
            handlePageChange={handleAIClick}
            selectedFunc={() => pathnames.includes('centerline-ai')}
          />
        ) : null}
        {currentProject ? (
          <DrawerNestedList
            key={2}
            title="Email"
            listIndex={2}
            handleDrawer={handleDrawerItemClick}
            handleNestedLists={toggleEmailMenu}
            selected={drawerIndex}
            icon={<EmailOutlined />}
            drawerOpen={drawerOpen}
            menuOpen={emailMenuOpen}
            subtitles={emailMenuItems}
          />
        ) : (
          <DrawerListItem
            key={2}
            title="My Notification Inbox"
            listIndex={2}
            onClick={handleDrawerItemClick}
            selectedIndex={drawerIndex}
            icon={<EmailOutlined />}
            drawerOpen={drawerOpen}
            handlePageChange={handleMyNotificationsClick}
          />
        )}
        <DrawerListItem
          key={8}
          title="My Work"
          listIndex={8}
          onClick={handleDrawerItemClick}
          selectedIndex={drawerIndex}
          icon={<AssignmentTurnedInOutlined />}
          drawerOpen={drawerOpen}
          handlePageChange={handleMyWorkClick}
        />
        {currentProject && (
          <>
            <DrawerNestedList
              key={3}
              title="Design"
              listIndex={3}
              handleDrawer={handleDrawerItemClick}
              handleNestedLists={toggleDesignMenu}
              selected={drawerIndex}
              icon={<DesignIcon />}
              drawerOpen={drawerOpen}
              menuOpen={designMenuOpen}
              subtitles={designMenuItems}
            />
            {canViewBidding(productPackage) || user.isSiteAdmin ? (
              <DrawerNestedList
                key={4}
                title="Bidding"
                listIndex={4}
                handleDrawer={handleDrawerItemClick}
                handleNestedLists={toggleBiddingMenu}
                selected={drawerIndex}
                icon={<BiddingIcon />}
                drawerOpen={drawerOpen}
                menuOpen={biddingMenuOpen}
                subtitles={biddingMenuItems}
                disabled={!currentProject}
                initiateSelectedItem={initiateSelectedIndex}
              />
            ) : null}
            {canViewConstructionAndCloseout(productPackage) || user.isSiteAdmin ? (
              <div>
                <DrawerNestedList
                  key={5}
                  title="Construction"
                  listIndex={5}
                  handleDrawer={handleDrawerItemClick}
                  handleNestedLists={toggleConstructionMenu}
                  selected={drawerIndex}
                  icon={<ConstructionIcon />}
                  drawerOpen={drawerOpen}
                  menuOpen={constructionMenuOpen}
                  subtitles={constructionMenuItems}
                  disabled={!currentProject}
                  initiateSelectedItem={initiateSelectedIndex}
                />
                <DrawerNestedList
                  key={6}
                  title="Close Out"
                  listIndex={6}
                  handleDrawer={handleDrawerItemClick}
                  handleNestedLists={toggleCloseOutMenu}
                  selected={drawerIndex}
                  icon={<CloseOutIcon />}
                  drawerOpen={drawerOpen}
                  menuOpen={closeOutMenuOpen}
                  subtitles={closeOutMenuItems}
                  disabled={!currentProject}
                  initiateSelectedItem={initiateSelectedIndex}
                />
              </div>
            ) : null}
            {canViewProjectLifecycle() ? (
              <DrawerNestedList
                key={7}
                title="Storytelling"
                listIndex={7}
                handleDrawer={handleDrawerItemClick}
                handleNestedLists={toggleStorytellingMenu}
                selected={drawerIndex}
                icon={<AssignmentTurnedInOutlined style={{ marginLeft: -1 }} />}
                drawerOpen={drawerOpen}
                menuOpen={storytellingMenuOpen}
                subtitles={storytellingMenuItems}
                disabled={!currentProject}
                initiateSelectedItem={initiateSelectedIndex}
              />
            ) : null}
          </>
        )}
      </List>
      {drawerOpen && (
        <Button onClick={handleSignOut} className={classes.exitButton}>
          <ExitToApp style={{ marginRight: '16px', transform: 'scaleX(-1)', color: '#73A3FF' }} />
          Sign Out
        </Button>
      )}
      <Hidden smUp>
        {drawerOpen && (
          <div
            style={{
              position: 'fixed',
              width: `calc(100% - ${PRIMARY_NAVIGATION_WIDTH_OPEN}px)`,
              height: '100%',
              top: 0,
              right: 0,
            }}
            onClick={toggleDrawer}
          />
        )}
      </Hidden>
    </>
  );

  return (
    <>
      {!isPublicPage() && !isPdfViewer() && (
        <div
          className={clsx(
            'drawer',
            location.pathname === '/main/login' ||
              location.pathname === '/main/register' ||
              location.pathname === '/main/forgot-password-confirm' ||
              location.pathname === '/main/change-email-confirm' ||
              location.pathname === '/main/manage-reminders' ||
              location.pathname.includes('/a3/print')
              ? classes.hidden
              : '',
          )}
        >
          <Hidden smUp>
            <CssBaseline />
            <AppBar position="fixed" className={classes.appBar}>
              <Toolbar>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={toggleDrawer}
                  className={classes.menuButton}
                >
                  <MenuIcon />
                </IconButton>
                <Typography variant="h6" noWrap>
                  CENTERLINE
                </Typography>
              </Toolbar>
            </AppBar>
          </Hidden>
          {/* Mobile drawer */}
          <Hidden smUp implementation="js">
            <Drawer
              variant="temporary"
              anchor={theme.direction === 'rtl' ? 'right' : 'left'}
              open={drawerOpen}
              onClose={toggleDrawer}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
              classes={{
                paper: clsx({
                  [classes.drawerOpen]: drawerOpen,
                  [classes.drawerClose]: !drawerOpen,
                }),
              }}
            >
              {drawer}
            </Drawer>
          </Hidden>

          {/* Desktop drawer */}
          <Hidden xsDown implementation="js">
            <Drawer
              variant="permanent"
              className={clsx(classes.drawer, {
                [classes.drawerOpen]: drawerOpen,
                [classes.drawerClose]: !drawerOpen,
              })}
              classes={{
                paper: clsx({
                  [classes.drawerOpen]: drawerOpen,
                  [classes.drawerClose]: !drawerOpen,
                }),
              }}
            >
              {drawer}
            </Drawer>
          </Hidden>
        </div>
      )}
    </>
  );
}
